import React, { useEffect, useState } from "react";
import VsOpsLayout from "./VsOpsLayout";
import { VsOpsRequireAuth } from "./VsOpsLoginPage";
import { vsOpsApi } from "./api";
import VsOpsPaymentProvidersDrawer from "./VsOpsPaymentProvidersDrawer";

const NUMERIC_SETTING_LABELS = {
  markup_percent: "Наценка СБП, %",
  visoria_funding_rate: "Курс завода на Visoria (₽ за $1)",
  payment_commission_percent: "Комиссия СБП, %",
  visoria_balance_alert_threshold: "Порог алерта баланса Visoria",
  crypto_markup_percent: "Наценка для крипты, %",
  crypto_commission_percent: "Комиссия криптоплатежа, %",
};

const BOOLEAN_SETTING_LABELS = {
  testing_period_enabled: "Период тестирования",
};

function VsOpsSettingsPage() {
  const [values, setValues] = useState({
    markup_percent: "",
    visoria_funding_rate: "",
    payment_commission_percent: "",
    crypto_markup_percent: "",
    crypto_commission_percent: "",
    visoria_balance_alert_threshold: "",
    testing_period_enabled: false,
    sbp_provider: "aurapay",
  });
  const [meta, setMeta] = useState({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [totpToken, setTotpToken] = useState("");
  const [session, setSession] = useState(null);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [activeSbpLabel, setActiveSbpLabel] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [settings, sessionData, payments] = await Promise.all([
        vsOpsApi.getSettings(),
        vsOpsApi.getSession(),
        vsOpsApi.getPaymentProviders().catch(() => null),
      ]);
      setValues(settings.values);
      setMeta(settings.meta);
      setSession(sessionData);
      if (payments?.activeSbpProvider) {
        const active = payments.providers?.find(
          (item) => item.id === payments.activeSbpProvider,
        );
        setActiveSbpLabel(active?.label || payments.activeSbpProvider);
      }
    } catch (loadError) {
      setError(loadError.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const { sbp_provider: _sbp, ...rest } = values;
      await vsOpsApi.updateSettings(rest);
      setMessage("Настройки сохранены");
      await load();
    } catch (saveError) {
      setError(saveError.message || "Ошибка сохранения");
    }
  };

  const setup2fa = async () => {
    setError("");
    try {
      const result = await vsOpsApi.setup2fa();
      setQrDataUrl(result.qrDataUrl);
    } catch (setupError) {
      setError(setupError.message || "Ошибка 2FA");
    }
  };

  const enable2fa = async () => {
    setError("");
    try {
      await vsOpsApi.enable2fa(totpToken.trim());
      setMessage("2FA включена");
      setQrDataUrl("");
      setTotpToken("");
      await load();
    } catch (enableError) {
      setError(enableError.message || "Не удалось включить 2FA");
    }
  };

  return (
    <VsOpsRequireAuth>
      <VsOpsLayout title="Настройки">
        {loading ? <p className="vs-ops-muted">Загрузка…</p> : null}
        {error ? <p className="vs-ops-error">{error}</p> : null}
        {message ? <p className="vs-ops-success">{message}</p> : null}

        <section className="vs-ops-settings-block">
          <h2 className="vs-ops-subtitle" style={{ marginTop: 0 }}>
            Платёжные системы
          </h2>
          <p className="vs-ops-muted">
            Активный фиат-шлюз:{" "}
            <strong>{activeSbpLabel || values.sbp_provider || "—"}</strong>
            . Переключение на лету; уже созданные счета дообрабатываются своим
            шлюзом. Platega по умолчанию — универсальная пейформа (СБП + карта).
          </p>
          <button
            type="button"
            className="vs-ops-btn"
            onClick={() => setPaymentsOpen(true)}
          >
            Открыть шторку платёжных систем
          </button>
        </section>

        <form className="vs-ops-settings-form" onSubmit={saveSettings}>
          {Object.entries(NUMERIC_SETTING_LABELS).map(([key, label]) => (
            <label key={key} className="vs-ops-field">
              <span>{label}</span>
              <input
                type="number"
                step="0.01"
                value={values[key] ?? ""}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [key]: event.target.value }))
                }
                required
              />
              {meta[key]?.description ? (
                <small className="vs-ops-muted">{meta[key].description}</small>
              ) : null}
            </label>
          ))}

          {Object.entries(BOOLEAN_SETTING_LABELS).map(([key, label]) => (
            <label key={key} className="vs-ops-field vs-ops-field-toggle">
              <span>{label}</span>
              <input
                type="checkbox"
                checked={Boolean(values[key])}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [key]: event.target.checked,
                  }))
                }
              />
              {meta[key]?.description ? (
                <small className="vs-ops-muted">{meta[key].description}</small>
              ) : null}
            </label>
          ))}

          <button type="submit" className="vs-ops-btn">
            Сохранить настройки
          </button>
        </form>

        <section className="vs-ops-settings-block">
          <h2 className="vs-ops-subtitle">Двухфакторная аутентификация</h2>
          <p className="vs-ops-muted">
            Статус: {session?.user?.totpEnabled ? "включена" : "выключена"}
          </p>
          {!session?.user?.totpEnabled ? (
            <>
              <button type="button" className="vs-ops-btn vs-ops-btn-secondary" onClick={setup2fa}>
                Настроить 2FA
              </button>
              {qrDataUrl ? (
                <div className="vs-ops-2fa-setup">
                  <img src={qrDataUrl} alt="QR для 2FA" />
                  <label className="vs-ops-field">
                    <span>Код из приложения</span>
                    <input
                      value={totpToken}
                      onChange={(event) => setTotpToken(event.target.value)}
                      placeholder="000000"
                    />
                  </label>
                  <button type="button" className="vs-ops-btn" onClick={enable2fa}>
                    Включить 2FA
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>

        <section className="vs-ops-settings-block">
          <h2 className="vs-ops-subtitle">Формула прибыли</h2>
          <p className="vs-ops-muted">
            Чистая прибыль = (сумма оплаты − комиссия СБП) − (USD товара × курс завода на Visoria).
            Наценка и курс Aura фиксируются в заказе в момент продажи.
          </p>
        </section>

        <VsOpsPaymentProvidersDrawer
          open={paymentsOpen}
          onClose={() => setPaymentsOpen(false)}
          onSaved={(state) => {
            const active = state.providers?.find(
              (item) => item.id === state.activeSbpProvider,
            );
            setActiveSbpLabel(active?.label || state.activeSbpProvider);
            setValues((current) => ({
              ...current,
              sbp_provider: state.activeSbpProvider,
            }));
          }}
        />
      </VsOpsLayout>
    </VsOpsRequireAuth>
  );
}

export default VsOpsSettingsPage;
